import * as styles from './FileUploadDialog.module.less';
import { IUploadFile } from "@/twapp/server/FileEndpoint";
import { TromanDialog, TromanDialogBody, TromanDialogFooter, TromanDialogTitleHeader } from '../../TromanDialog';
import { ButtonVariants, TromanButton } from '../../Buttons';
import { IInvalidFile, IPreparedUpload } from './fileUpload';
import { WithOptional } from '@/common/typeHelpers';
import { useState } from 'react';
import { FileValidationResult } from '../model/fileValidation';
import { TogglePanel } from '../../TogglePanel';
import _ from 'lodash';
import { formatFileCount } from '../model/fileStats';

export interface IFileUploadDialogProps {
    files: IPreparedUpload;
    onClose: (upload?: IUploadFile[]) => void;
}

function getValidationText(file: IInvalidFile) {
    switch (file.error) {
        case FileValidationResult.InvalidCharacter: return 'Felaktigt namn';
        case FileValidationResult.InvalidExtension: return 'Ej godkänd filtyp';
        case FileValidationResult.EmptyName: return 'Saknar namn';
        case FileValidationResult.Duplicate: return 'Dubblett';
        case FileValidationResult.TooLarge: return 'Dokumentet är för stort';
        default: return '';
    }
}

const FileUploadInner = ({files, onClose}: IFileUploadDialogProps) => {
    const [replace, setReplace] = useState(false);

    return (
        <>
            <TromanDialogTitleHeader title='Ladda upp dokument'/>
            <TromanDialogBody>
                <div className={styles.section}>
                    {formatFileCount(files.valid.length + files.existing.length + files.invalid.length)} utvalda för uppladdning.
                </div>
                {
                    files.valid.length > 0 && (
                        <div className={styles.section}>
                            <div className={styles.title}>Nya dokument</div>
                            <div className={styles.description}>{formatFileCount(files.valid.length)} kommer att laddas upp</div>
                            <TogglePanel title='Visa detaljer'>
                                {
                                    files.valid.map(x => (
                                        <div key={x.FilePath}>{x.FilePath}</div>
                                    ))
                                }
                            </TogglePanel>
                        </div>
                    )

                }
                {
                    files.existing.length > 0 && (
                        <div className={styles.section}>
                            <div className={styles.title}>Ersätt dokument</div>
                            <div className={styles.description}>{formatFileCount(files.existing.length)} finns redan, men kan skrivas över</div>
                            <div className='checkbox'>
                                <label>
                                    <input type='checkbox' checked={replace} onChange={x => setReplace(x.target.checked)}/>
                                    Skriv över
                                </label>
                            </div>
                            <TogglePanel title='Visa detaljer' initial={true}>
                                {
                                    files.existing.map(x => (
                                        <div key={x.existing.id}>{x.existing.fullPath}</div>
                                    ))
                                }
                            </TogglePanel>
                        </div>
                    )

                }
                {
                    files.invalid.length > 0 && (
                        <div className={styles.section}>
                            <div className={styles.title}>Ogiltiga dokument</div>
                            <div className={styles.description}>{formatFileCount(files.invalid.length)} kan inte laddas upp</div>
                            <TogglePanel title='Visa detaljer'>
                                <table className={styles.invalidTable}>
                                    <tbody>
                                        {
                                            files.invalid.map(x => (
                                                <tr key={x.path}>
                                                    <td>{x.path}</td>
                                                    <td>{getValidationText(x)}</td>
                                                </tr>
                                            ))
                                        }
                                    </tbody>
                                </table>
                            </TogglePanel>
                        </div>
                    )
                }
            </TromanDialogBody>
            <TromanDialogFooter>
                <TromanButton
                    text='Ladda upp' 
                    variant={ButtonVariants.Primary}
                    disabled={files.valid.length === 0 && (files.existing.length === 0 || !replace)}
                    onClick={() => onClose(_.concat(files.valid, replace ? files.existing.map(x => x.replaceCmd) : []))}/>
                <TromanButton text='Avbryt' variant={ButtonVariants.Default} onClick={() => onClose()}/>
            </TromanDialogFooter>
        </>
    );
}

export const FileUploadDialog = ({files, ...props}: WithOptional<IFileUploadDialogProps, "files">) => {
    return (
        <TromanDialog open={!!files}>
            { files && (<FileUploadInner files={files} {...props}/>) }
        </TromanDialog>
    )
}